import React, { useState } from 'react';
import { getPlanPricing, getPlanDisplayName } from '../services/subscriptionService';

const AdvancedPricingTiers = ({ 
    currentPlan = 'free', 
    onSelectPlan, 
    loading = false,
    className = '' 
}) => {
    const [hoveredPlan, setHoveredPlan] = useState(null);
    const plans = ['free', 'basic', 'unlimited'];
    
    // Define all possible features across all plans - basic features first, then premium
    const allFeatures = [
        'Basic features',
        'Chef agent features',
        'Custom chef interactions',
        'AI tips & reminders',
        'Chef summaries',
        'Menu planning',
        'Multi-recipe sessions',
        'Grocery list generation',
        'Advanced analytics',
        'Priority support'
    ];

    // Get intelligent recipe feature based on plan
    const getRecipeFeature = (planType) => {
        // All users now get unlimited recipes
        return 'Unlimited recipes';
    };

    const getPlanFeatures = (planType) => {
        const pricing = getPlanPricing(planType);
        return pricing.features;
    };

    const isFeatureIncluded = (planType, feature) => {
        // Define which features are included in each plan
        const planFeatureMap = {
            'free': ['Basic features'],
            'basic': [
                'Basic features',
                'Chef agent features',
                'Custom chef interactions',
                'AI tips & reminders',
                'Chef summaries'
            ],
            'unlimited': [
                'Menu planning',
                'Multi-recipe sessions', 
                'Grocery list generation',
                'Advanced analytics',
                'Priority support',
                'Chef agent features',
                'Custom chef interactions',
                'AI tips & reminders',
                'Chef summaries',
                'Basic features'
            ],
            'beta': [
                'Menu planning',
                'Multi-recipe sessions', 
                'Grocery list generation',
                'Advanced analytics',
                'Priority support',
                'Chef agent features',
                'Custom chef interactions',
                'AI tips & reminders',
                'Chef summaries',
                'Basic features'
            ],
            'admin': [
                'Menu planning',
                'Multi-recipe sessions', 
                'Grocery list generation',
                'Advanced analytics',
                'Priority support',
                'Chef agent features',
                'Custom chef interactions',
                'AI tips & reminders',
                'Chef summaries',
                'Basic features'
            ],
            'family': [
                'Menu planning',
                'Multi-recipe sessions', 
                'Grocery list generation',
                'Advanced analytics',
                'Priority support',
                'Chef agent features',
                'Custom chef interactions',
                'AI tips & reminders',
                'Chef summaries',
                'Basic features'
            ]
        };
        
        return planFeatureMap[planType]?.includes(feature) || false;
    };

    const getTextAlignment = (planIndex, hoveredIndex) => {
        if (hoveredIndex === null || hoveredIndex === -1) return 'text-center';
        
        if (planIndex < hoveredIndex) return 'text-left';
        if (planIndex > hoveredIndex) return 'text-right';
        return 'text-center';
    };

    const getTextAlignmentStyle = (planIndex, hoveredIndex) => {
        if (hoveredIndex === null || hoveredIndex === -1) return { 
            textAlign: 'center'
        };
        
        if (planIndex < hoveredIndex) return { 
            textAlign: 'left'
        };
        if (planIndex > hoveredIndex) return { 
            textAlign: 'right'
        };
        return { 
            textAlign: 'center'
        };
    };

    const getPlanButtonText = (planType) => {
        if (planType === currentPlan) {
            return 'Current Plan';
        }
        
        // For admin/family/beta users, disable all upgrade buttons
        if (currentPlan === 'admin' || currentPlan === 'family' || currentPlan === 'beta') {
            return 'Coming Soon';
        }
        
        switch (planType) {
            case 'free':
                return 'Get Started';
            case 'basic':
                return 'Upgrade to Basic';
            case 'unlimited':
                return 'Upgrade to Unlimited';
            default:
                return 'Select Plan';
        }
    };

    const getPlanButtonClass = (planType) => {
        const baseClass = "px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none";
        
        if (planType === currentPlan) {
            return `${baseClass} bg-neutral-50 text-neutral-900 hairline cursor-default`;
        }
        
        // For admin/family/beta users, disable all upgrade buttons
        if (currentPlan === 'admin' || currentPlan === 'family' || currentPlan === 'beta') {
            return `${baseClass} bg-neutral-300 text-neutral-500 cursor-not-allowed`;
        }
        
        return `${baseClass} bg-[#1b263b] text-white hover:opacity-60`;
    };

    const isButtonDisabled = (planType) => {
        return loading || planType === currentPlan || 
               (currentPlan === 'admin' || currentPlan === 'family' || currentPlan === 'beta');
    };

    return (
        <div className={`w-full ${className}`}>
            {/* Header */}
            <div className="text-center mb-8">
                <h2 className="font-sans text-3xl font-medium text-[#1b263b] mb-2">
                    Choose Your Plan
                </h2>
                <p className="font-sans text-base text-neutral-600">
                    Select the plan that best fits your cooking needs
                </p>
            </div>

            {/* Pricing Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 bg-neutral-0 hairline p-2">
                {plans.map((planType, planIndex) => {
                    const pricing = getPlanPricing(planType);
                    const planName = getPlanDisplayName(planType);
                    const isCurrent = planType === currentPlan;
                    const isHovered = hoveredPlan === planType;
                    const isPopular = planType === 'basic';

                    return (
                        <div 
                            key={planType}
                            className={`relative bg-white border border-black transition-all duration-700 ease-out ${
                                isHovered ? 'z-10 shadow-lg scale-[1.01]' : 'z-0 scale-100'
                            }`}
                            onMouseEnter={() => setHoveredPlan(planType)}
                            onMouseLeave={() => setHoveredPlan(null)}
                        >
                            {/* Popular Badge */}
                            {isPopular && (
                                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-20">
                                    <span className="bg-[#1b263b] text-white px-3 py-1 text-xs font-mono uppercase tracking-wider">
                                        Most Popular
                                    </span>
                                </div>
                            )}

                            {/* Plan Header */}
                            <div className="p-6 border-b border-neutral-200">
                                <div 
                                    className="transition-all duration-700 ease-out"
                                    style={getTextAlignmentStyle(planIndex, plans.findIndex(p => p === hoveredPlan))}
                                >
                                    <h3 className="font-sans text-xl font-medium text-[#1b263b] mb-2">
                                        {planName}
                                    </h3>
                                    <div className="mb-4">
                                        {pricing.price === 0 ? (
                                            <span className="font-sans text-3xl font-medium text-[#1b263b]">Free</span>
                                        ) : (
                                            <div>
                                                <span className="font-sans text-3xl font-medium text-[#1b263b]">
                                                    ${pricing.price}
                                                </span>
                                                <span className="font-sans text-base text-neutral-600 ml-1">
                                                    /{pricing.period}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Recipe Count */}
                                    <div className="mb-4">
                                        {pricing.recipes === -1 ? (
                                            <span className="font-mono text-sm uppercase tracking-wider text-[#1b263b]">
                                                Unlimited Recipes
                                            </span>
                                        ) : (
                                            <span className="font-mono text-sm uppercase tracking-wider text-[#1b263b]">
                                                {pricing.recipes} Recipe{pricing.recipes !== 1 ? 's' : ''}
                                                {planType === 'basic' && ' / Month'}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Features List */}
                            <div className="p-0">
                                <div className="space-y-0">
                                    {/* Recipe Feature - Always included for each plan */}
                                    <div className="relative">
                                        <div 
                                            className="transition-all duration-700 ease-out bg-black p-4"
                                            style={getTextAlignmentStyle(planIndex, plans.findIndex(p => p === hoveredPlan))}
                                        >
                                            <div className="font-sans text-sm text-white">
                                                <span className="inline-flex items-center">
                                                    <span className="text-white mr-2">✓</span>
                                                    {getRecipeFeature(planType)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Other Features */}
                                    {allFeatures.map((feature, featureIndex) => {
                                        const isIncluded = isFeatureIncluded(planType, feature);
                                        
                                        return (
                                            <div key={featureIndex} className="relative">
                                                {/* Dividing line - extends full width */}
                                                <div className="absolute -top-px left-0 right-0 h-px bg-neutral-200"></div>
                                                
                                                <div 
                                                    className={`transition-all duration-700 ease-out p-4 ${
                                                        isIncluded ? 'bg-black' : 'bg-white'
                                                    }`}
                                                    style={getTextAlignmentStyle(planIndex, plans.findIndex(p => p === hoveredPlan))}
                                                >
                                                    <div className={`font-sans text-sm transition-all duration-700 ease-out ${
                                                        isIncluded 
                                                            ? 'text-white' 
                                                            : 'text-neutral-900'
                                                    }`}>
                                                        {isIncluded ? (
                                                            <span className="inline-flex items-center">
                                                                <span className="text-white mr-2">✓</span>
                                                                {feature}
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center">
                                                                <span className="text-neutral-500 mr-2">✗</span>
                                                                <span className="opacity-50">{feature}</span>
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Action Button */}
                            <div className="p-6 border-t border-neutral-200">
                                <div 
                                    className="transition-all duration-700 ease-out"
                                    style={getTextAlignmentStyle(planIndex, plans.findIndex(p => p === hoveredPlan))}
                                >
                                    <button
                                        onClick={() => onSelectPlan(planType)}
                                        disabled={isButtonDisabled(planType)}
                                        className={getPlanButtonClass(planType)}
                                        style={{ 
                                            backgroundColor: isCurrent ? '#F9F8F6' : 
                                                           (currentPlan === 'admin' || currentPlan === 'family' || currentPlan === 'beta') ? '#d1d5db' : '#1b263b',
                                            color: isCurrent ? '#0D0D0D' : 
                                                   (currentPlan === 'admin' || currentPlan === 'family' || currentPlan === 'beta') ? '#6b7280' : '#FFFFFF'
                                        }}
                                    >
                                        {loading ? 'Processing...' : getPlanButtonText(planType)}
                                    </button>
                                </div>

                                {/* Current Plan Indicator */}
                                {isCurrent && (
                                    <div className="mt-3">
                                        <span className="font-mono text-xs uppercase tracking-wider text-neutral-600">
                                            Your Current Plan
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Footer Note */}
            <div className="mt-6 text-center">
                <p className="font-sans text-sm text-neutral-600">
                    All plans include access to the 4RKED cooking platform and community features.
                </p>
            </div>
        </div>
    );
};

export default AdvancedPricingTiers;
